import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AudioTrack } from "@/components/AudioTrack";
import { ArrowRight, Bookmark, Check, Copy, Download, ExternalLink, Lock, PenLine, Play, Share2, X } from "lucide-react";
import { OFFER } from "@/config/offer";

const PROGRESS_KEY = "qr-audio-max-completed";
const BOOKMARK_TIP_KEY = "qr-bookmark-tip-dismissed";
const RECAP_DISMISSED_KEY = "qr-recap-dismissed-for";
const CTA_REVEALED_KEY = "qr-cta-revealed";

// Prose helper (mirrors Landing) — sentence-per-line with paragraph breaks.
const Prose = ({ text, className = "" }) => {
  const paragraphs = text.split(/\n\n+/);
  const items = [];
  paragraphs.forEach((paragraph, pi) => {
    const sentences = paragraph.trim().split(/(?<=[.?!])\s+(?=["'A-Z])/);
    sentences.forEach((sentence, si) => {
      const isFirstOfLaterParagraph = pi > 0 && si === 0;
      items.push(
        <p
          key={`${pi}-${si}`}
          className={`${className} ${isFirstOfLaterParagraph ? "mt-7" : ""}`}
        >
          {sentence.split("\n").map((line, k, arr) => (
            <React.Fragment key={k}>
              {line}
              {k < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      );
    });
  });
  return items;
};

export default function Success() {
  // Highest index that has been listened to end-to-end. -1 means none completed.
  const [maxCompleted, setMaxCompleted] = useState(-1);
  const [showBookmarkTip, setShowBookmarkTip] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [shareStatus, setShareStatus] = useState("idle"); // idle | copied | shared
  const [previewMode, setPreviewMode] = useState(false);
  const [ctaRevealed, setCtaRevealed] = useState(false);

  useEffect(() => {
    // Preview mode — appended as ?preview=1 in the URL. Shows a floating panel
    // that lets you cycle through progress states without paying / real listening.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("preview") === "1") setPreviewMode(true);
    } catch (_) {}
    try {
      const raw = localStorage.getItem(PROGRESS_KEY);
      let n = -1;
      if (raw !== null) {
        const parsed = parseInt(raw, 10);
        if (Number.isInteger(parsed) && parsed >= -1) {
          n = parsed;
          setMaxCompleted(parsed);
        }
      }
      // Show bookmark tip unless previously dismissed.
      const dismissed = localStorage.getItem(BOOKMARK_TIP_KEY);
      if (!dismissed) setShowBookmarkTip(true);
      // Show recap only for returning users mid-journey (0..4 completed) and
      // only if they haven't dismissed the recap at this exact stage.
      const recapDismissedFor = localStorage.getItem(RECAP_DISMISSED_KEY);
      if (n >= 0 && n < OFFER.audioTracks.length - 1 && recapDismissedFor !== String(n)) {
        setShowRecap(true);
      }
      // Persist the "user tapped the 7th tile" gate across sessions.
      if (localStorage.getItem(CTA_REVEALED_KEY) === "1") setCtaRevealed(true);
    } catch (_) {}
  }, []);

  // ── Diagnostic tracking — Meta Pixel custom event (post-completion) ──
  // Fires FL_Conversation_Click when the visitor clicks the Continue the
  // Conversation CTA. Uses a delegated document-level capture-phase
  // listener on [data-fl-conversation] so the anchor's default
  // navigation to Calendly is not modified in any way. Falls back
  // silently if fbq is unavailable. Does not touch any other Pixel
  // events, PageView, or Purchase / CAPI flow.
  useEffect(() => {
    const onClick = (e) => {
      const target = e.target && e.target.closest && e.target.closest("[data-fl-conversation]");
      if (!target) return;
      try {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("trackCustom", "FL_Conversation_Click");
        }
      } catch (_) {
        // never let tracking interfere with navigation
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("click", onClick, true);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("click", onClick, true);
      }
    };
  }, []);

  const applyPreviewState = (state) => {
    try {
      if (state === "fresh") {
        localStorage.removeItem(PROGRESS_KEY);
        localStorage.removeItem(BOOKMARK_TIP_KEY);
        localStorage.removeItem(RECAP_DISMISSED_KEY);
        localStorage.removeItem(CTA_REVEALED_KEY);
      } else if (state === "mid") {
        localStorage.setItem(PROGRESS_KEY, "2");
        localStorage.setItem(BOOKMARK_TIP_KEY, "1");
        localStorage.removeItem(RECAP_DISMISSED_KEY);
        localStorage.removeItem(CTA_REVEALED_KEY);
      } else if (state === "done") {
        localStorage.setItem(PROGRESS_KEY, "5");
        localStorage.setItem(BOOKMARK_TIP_KEY, "1");
        localStorage.removeItem(CTA_REVEALED_KEY);
      }
    } catch (_) {}
    window.location.reload();
  };

  const dismissBookmarkTip = () => {
    setShowBookmarkTip(false);
    try {
      localStorage.setItem(BOOKMARK_TIP_KEY, "1");
    } catch (_) {}
  };

  const dismissRecap = () => {
    setShowRecap(false);
    try {
      localStorage.setItem(RECAP_DISMISSED_KEY, String(maxCompleted));
    } catch (_) {}
  };

  const jumpToNext = () => {
    const nextIndex = maxCompleted + 1;
    const el = document.querySelector(`[data-testid="audio-track-${nextIndex}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    dismissRecap();
  };

  const handleComplete = useCallback((index) => {
    setMaxCompleted((prev) => {
      const next = Math.max(prev, index);
      try {
        localStorage.setItem(PROGRESS_KEY, String(next));
      } catch (_) {}
      return next;
    });
  }, []);

  const revealCtaAndScroll = () => {
    setCtaRevealed(true);
    try {
      localStorage.setItem(CTA_REVEALED_KEY, "1");
    } catch (_) {}
    // Wait one frame for the sections to render, then scroll into view.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="section-completion"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const total = OFFER.audioTracks.length;
  const completedCount = Math.max(0, maxCompleted + 1);
  const allComplete = useMemo(() => completedCount >= total, [completedCount, total]);
  const nextTrack = maxCompleted + 1 < total ? OFFER.audioTracks[maxCompleted + 1] : null;

  const shareText = `I just completed the Four Levels Experience — a thoughtful hour that gave me a new perspective. You might appreciate it too.`;
  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.origin + "/" : "";
    const shareData = {
      title: "The Four Levels Experience",
      text: shareText,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShareStatus("shared");
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setShareStatus("copied");
      }
    } catch (_) {
      // User cancelled share or clipboard failed — no-op.
    }
    setTimeout(() => setShareStatus("idle"), 2500);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="success-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-0 md:h-16 flex flex-col md:flex-row items-center md:justify-between gap-2 md:gap-4">
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3"
            data-testid="success-brand"
          >
            <img src={OFFER.logoUrl} alt={OFFER.brand} className="h-8 md:h-9 w-auto" />
          </a>
          <span className="text-[11px] md:text-xs uppercase tracking-[0.22em] md:tracking-[0.24em] text-muted-foreground">
            The Four Levels Experience
          </span>
        </div>
      </header>

      {/* Bookmark tip — dismissible */}
      {showBookmarkTip && (
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-6" data-testid="bookmark-tip">
          <div className="flex items-start gap-3 rounded-2xl bg-brand-turquoise/10 border border-brand-turquoise/25 px-5 py-4">
            <Bookmark className="h-4 w-4 text-brand-turquoise shrink-0 mt-0.5" />
            <p className="text-sm md:text-[15px] leading-relaxed text-foreground/85 flex-1">
              <span className="font-medium text-brand-turquoise">Tip:</span> bookmark this page — it's your permanent home for the content.
            </p>
            <button
              type="button"
              onClick={dismissBookmarkTip}
              aria-label="Dismiss bookmark tip"
              className="text-foreground/50 hover:text-foreground transition-colors shrink-0 -mr-1"
              data-testid="bookmark-tip-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Progress Recap — shown only for returning mid-journey visitors */}
      {showRecap && nextTrack && (
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-4" data-testid="progress-recap">
          <div className="flex items-center gap-3 rounded-2xl bg-card border border-brand-turquoise/30 px-5 py-4 shadow-sm">
            <div className="h-9 w-9 shrink-0 rounded-full bg-brand-turquoise/15 grid place-items-center text-brand-turquoise">
              <Play className="h-4 w-4 ml-0.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                Pick up where you left off
              </p>
              <p className="text-[15px] md:text-base leading-tight text-foreground mt-0.5 truncate">
                {nextTrack.title}
                {nextTrack.subtitle ? (
                  <span className="text-muted-foreground"> — {nextTrack.subtitle}</span>
                ) : null}
              </p>
            </div>
            <button
              type="button"
              onClick={jumpToNext}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-turquoise text-primary-foreground px-4 py-2 text-sm font-medium btn-lift focus:outline-none focus:ring-2 focus:ring-brand-turquoise/50 shrink-0"
              data-testid="progress-recap-resume"
            >
              Resume
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={dismissRecap}
              aria-label="Dismiss recap"
              className="text-foreground/50 hover:text-foreground transition-colors shrink-0 -mr-1"
              data-testid="progress-recap-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Welcome ──────────────────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-10">
        <p className="text-xs uppercase tracking-[0.24em] text-brand-turquoise">
          Welcome
        </p>
        <h1
          className="font-serif text-4xl md:text-5xl tracking-tight mt-5 leading-[1.1]"
          data-testid="welcome-heading"
        >
          {OFFER.welcome.headline}
        </h1>
        <div
          className="mt-8 md:mt-10 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]"
          data-testid="welcome-body"
        >
          <Prose text={OFFER.welcome.body} />
        </div>
      </section>

      {/* ── Recognition Guide ────────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
          Companion Workbook
        </p>
        <a
          href={OFFER.files.workbook}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-5 flex items-start justify-between gap-4 rounded-2xl border border-border bg-card p-6 md:p-7 hover:border-brand-turquoise/40 transition-colors duration-300"
          data-testid="download-workbook-btn"
        >
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-brand-turquoise/12 grid place-items-center shrink-0">
              <PenLine className="h-5 w-5 text-brand-turquoise" />
            </div>
            <div>
              <h3 className="font-serif text-xl md:text-2xl tracking-tight">
                Download Recognition Guide
              </h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed max-w-md">
                A companion workbook for capturing your thoughts, insights, and reflections
                throughout the experience.
              </p>
            </div>
          </div>
          <Download className="h-5 w-5 text-muted-foreground mt-2 group-hover:text-brand-turquoise transition-colors duration-300 shrink-0" />
        </a>
      </section>

      {/* ── Conversations ────────────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14" id="conversations">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
              Conversations
            </p>
            <h2
              className="font-serif text-3xl md:text-4xl tracking-tight mt-3"
              data-testid="conversations-heading"
            >
              Six guided audio conversations.
            </h2>
          </div>
          <p
            className="text-sm text-muted-foreground tabular-nums"
            data-testid="conversation-progress"
          >
            {completedCount} of {total} completed
          </p>
        </div>

        {/* Slim progress bar */}
        <div
          className="mt-4 h-1 w-full rounded-full bg-muted overflow-hidden"
          data-testid="progress-bar"
          aria-label={`Progress: ${completedCount} of ${total}`}
        >
          <div
            className="h-full bg-brand-turquoise transition-all duration-700"
            style={{ width: `${(completedCount / total) * 100}%` }}
          />
        </div>

        <p className="mt-6 text-sm text-muted-foreground max-w-[52ch]">
          Each conversation unlocks the next once you've listened all the way through. You can
          revisit any conversation you've already completed.
        </p>

        <div className="grid grid-cols-1 gap-4 md:gap-5 mt-8 md:mt-10">
          {OFFER.audioTracks.map((t, i) => (
            <AudioTrack
              key={i}
              track={t}
              index={i}
              locked={i > maxCompleted + 1}
              completed={i <= maxCompleted}
              onComplete={handleComplete}
              earlyCompleteAt={i === OFFER.audioTracks.length - 1 ? 0.9 : undefined}
            />
          ))}

          {/* 7th tile — Continue. Locked until Audio 6 ≥90%. When active, it
              is the gate that reveals the Completion + Continue Journey sections. */}
          {allComplete ? (
            <button
              type="button"
              onClick={revealCtaAndScroll}
              className="group rounded-2xl border border-brand-turquoise/40 bg-brand-turquoise/8 hover:bg-brand-turquoise/12 hover:border-brand-turquoise/60 transition-colors duration-300 p-6 md:p-7 flex items-center gap-4 text-left min-h-[172px] focus:outline-none focus:ring-2 focus:ring-brand-turquoise/50"
              data-testid="continue-tile-unlocked"
            >
              <div className="h-12 w-12 shrink-0 rounded-full bg-brand-turquoise text-primary-foreground grid place-items-center btn-lift">
                <ArrowRight className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-[0.22em] text-brand-turquoise">
                  Continue
                </p>
                <p className="font-serif text-lg md:text-xl leading-tight text-foreground mt-1.5">
                  You've completed the six conversations.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Tap to see what comes next.
                </p>
              </div>
            </button>
          ) : (
            <div
              className="rounded-2xl border border-dashed border-border bg-muted/40 p-6 md:p-7 flex items-center gap-4 min-h-[172px]"
              data-testid="continue-tile-locked"
              aria-disabled="true"
            >
              <div className="h-12 w-12 shrink-0 rounded-full bg-muted grid place-items-center text-muted-foreground">
                <Lock className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  Continue
                </p>
                <p className="font-serif text-lg md:text-xl leading-tight text-foreground/80 mt-1.5">
                  Your next step is waiting.
                </p>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1.5">
                  Finish Conversation 6 to unlock
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Completion (only after user taps the 7th tile) ───────────────── */}
      {allComplete && ctaRevealed && (
        <section
          className="border-t border-border/40 mt-14 md:mt-20 scroll-mt-28 md:scroll-mt-20"
          data-testid="section-completion"
        >
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <p className="text-xs uppercase tracking-[0.24em] text-brand-turquoise">
              Completion
            </p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-5 leading-[1.15]">
              {OFFER.completion.headline}
            </h2>
            <div className="mt-8 md:mt-10 space-y-5 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]">
              <Prose text={OFFER.completion.body} />
            </div>

            {/* Share nudge — soft prompt to pass the experience along */}
            <div
              className="mt-12 md:mt-14 rounded-2xl border border-border bg-card px-6 md:px-8 py-6 md:py-7 flex flex-col sm:flex-row sm:items-center gap-4"
              data-testid="share-block"
            >
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  Share this experience
                </p>
                <p className="text-[15px] md:text-base leading-relaxed text-foreground/85 mt-2 max-w-[52ch]">
                  If someone came to mind while you listened, consider inviting them to their own hour of reflection.
                </p>
              </div>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 rounded-full bg-brand-turquoise text-primary-foreground px-5 py-2.5 text-sm font-medium btn-lift focus:outline-none focus:ring-2 focus:ring-brand-turquoise/50 shrink-0"
                data-testid="share-btn"
              >
                {shareStatus === "copied" ? (
                  <>
                    <Check className="h-4 w-4" />
                    Link copied
                  </>
                ) : shareStatus === "shared" ? (
                  <>
                    <Check className="h-4 w-4" />
                    Shared
                  </>
                ) : (
                  <>
                    {typeof navigator !== "undefined" && navigator.share ? (
                      <Share2 className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {typeof navigator !== "undefined" && navigator.share ? "Share" : "Copy link"}
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Continue the Conversation (only after user taps the 7th tile) ─── */}
      {allComplete && ctaRevealed && (
        <section
          className="border-t border-border/40 bg-muted/30"
          data-testid="section-continue"
        >
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
              Continue the Conversation
            </p>
            <h2
              className="font-serif text-3xl md:text-4xl tracking-tight mt-5 leading-[1.2] max-w-[28ch]"
              data-testid="continue-conversation-headline"
            >
              {OFFER.continueJourney.headline}
            </h2>

            <p className="mt-8 md:mt-10 text-[17px] md:text-lg leading-[1.75] text-foreground/85 max-w-[62ch]">
              {OFFER.continueJourney.body}
            </p>

            <div className="mt-10 md:mt-12">
              <a
                href={OFFER.continueJourney.url}
                target="_blank"
                rel="noopener noreferrer"
                data-fl-conversation="true"
                data-testid="continue-conversation-cta"
                className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 text-base font-medium transition-colors"
              >
                {OFFER.continueJourney.cta}
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            <div
              className="mt-14 md:mt-16 font-serif text-lg md:text-xl italic text-foreground/70 leading-relaxed max-w-[52ch] whitespace-pre-line"
              data-testid="continue-closing"
            >
              {OFFER.continueJourney.closing}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-border/40">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-8 text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} {OFFER.brand} &middot; Thank you for being here.
        </div>
      </footer>

      {/* Preview panel — only visible when ?preview=1 is in the URL. */}
      {previewMode && (
        <div
          className="fixed bottom-4 right-4 z-50 rounded-2xl border border-brand-turquoise/40 bg-background/95 backdrop-blur shadow-lg px-4 py-3 flex flex-col gap-2 max-w-[240px]"
          data-testid="preview-panel"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] text-brand-turquoise font-medium">
            Preview mode
          </p>
          <p className="text-xs text-muted-foreground leading-snug">
            Jump between states without paying. Currently: <span className="font-medium text-foreground">{maxCompleted + 1} / {OFFER.audioTracks.length}</span> complete.
          </p>
          <div className="flex flex-col gap-1.5 mt-1">
            <button
              type="button"
              onClick={() => applyPreviewState("fresh")}
              className="text-xs rounded-full border border-border bg-card hover:border-brand-turquoise/50 px-3 py-1.5 text-left transition-colors"
              data-testid="preview-state-fresh"
            >
              Fresh visitor
            </button>
            <button
              type="button"
              onClick={() => applyPreviewState("mid")}
              className="text-xs rounded-full border border-border bg-card hover:border-brand-turquoise/50 px-3 py-1.5 text-left transition-colors"
              data-testid="preview-state-mid"
            >
              Mid-journey (3 done)
            </button>
            <button
              type="button"
              onClick={() => applyPreviewState("done")}
              className="text-xs rounded-full border border-border bg-card hover:border-brand-turquoise/50 px-3 py-1.5 text-left transition-colors"
              data-testid="preview-state-done"
            >
              All complete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
